import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Brain, Zap, BarChart3 } from 'lucide-react';

const AITechnology: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const aiFeatures = [
    {
      icon: Brain,
      title: t('ai.whisper'),
      description: 'Transcrição de voz em tempo real com alta precisão',
      color: 'primary',
    },
    {
      icon: Zap,
      title: t('ai.gpt'),
      description: 'Respostas inteligentes e contextualizadas',
      color: 'secondary',
    },
    {
      icon: BarChart3,
      title: t('ai.dashboard'),
      description: 'Análise automática e insights em tempo real',
      color: 'accent',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('ai.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('ai.subtitle')}
          </p>
        </motion.div>

        {/* AI Features */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-3 gap-8 mb-16"
        >
          {aiFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            const colorClasses = {
              primary: 'bg-primary-100 text-primary-600',
              secondary: 'bg-secondary-100 text-secondary-600',
              accent: 'bg-accent-100 text-accent-600',
            };

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-gray-50 rounded-2xl p-8 text-center hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <div className={`w-20 h-20 rounded-full ${colorClasses[feature.color as keyof typeof colorClasses]} flex items-center justify-center mb-6 mx-auto`}>
                  <IconComponent size={40} />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* AI Process Flow */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-12 text-white"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-6">Fluxo de Processamento IA</h3>
            <p className="text-xl opacity-90">
              Como a inteligência artificial processa e responde às interações
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative">
            {/* Fluxo de etapas */}
            <motion.div
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎤</span>
              </div>
              <h4 className="text-lg font-semibold mb-2">Captura de Voz</h4>
              <p className="text-sm opacity-80">Captura e processamento do áudio</p>
            </motion.div>
            <motion.div
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.9 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-secondary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📝</span>
              </div>
              <h4 className="text-lg font-semibold mb-2">Transcrição</h4>
              <p className="text-sm opacity-80">Whisper converte voz em texto</p>
            </motion.div>
            <motion.div
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 1.0 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-accent-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🧠</span>
              </div>
              <h4 className="text-lg font-semibold mb-2">Processamento</h4>
              <p className="text-sm opacity-80">GPT gera resposta contextual</p>
            </motion.div>
            <motion.div
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 1.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🗣️</span>
              </div>
              <h4 className="text-lg font-semibold mb-2">Resposta</h4>
              <p className="text-sm opacity-80">Avatar responde naturalmente</p>
            </motion.div>
            {/* Linha gradiente centralizada */}
            <div className="absolute left-0 right-0 -bottom-8 flex justify-center pointer-events-none select-none">
              <div className="w-11/12 h-0.5 rounded-full bg-gradient-to-r from-primary-400 via-secondary-400 to-green-400 mx-auto" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AITechnology; 